import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

interface SystemPromptDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conversationId: string;
  currentPrompt: string;
  onSave: (prompt: string) => void;
}

export function SystemPromptDialog({
  open,
  onOpenChange,
  conversationId,
  currentPrompt,
  onSave,
}: SystemPromptDialogProps) {
  const [draft, setDraft] = useState(currentPrompt);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setDraft(currentPrompt);
  }, [open, currentPrompt]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const res = await fetch(`/api/conversations/${conversationId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ systemPrompt: draft }),
      });
      if (!res.ok) throw new Error("Failed to save");
      onSave(draft);
      onOpenChange(false);
      toast.success("System prompt updated");
    } catch {
      toast.error("Failed to save system prompt");
    } finally {
      setSaving(false);
    }
  }, [conversationId, draft, onSave, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>System Prompt</DialogTitle>
          <DialogDescription>
            Override the system prompt for this conversation only.
          </DialogDescription>
        </DialogHeader>
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Enter a custom system prompt for this conversation..."
          className="min-h-[150px] resize-y font-mono text-sm"
          rows={6}
        />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
